// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isVmiAdmin, isVmiUser } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import ProductAddToCartButton from "@synd/Overrides/Components/ProductAddToCartButton";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    BadgeModel,
    CartModel,
    OrderLineModel,
    OrderModel,
    ProductModel,
    WishListSettingsModel,
} from "@insite/client-framework/Types/ApiModels";
import CartLinesListHeader from "@synd/Overrides/Components/CartLinesListHeader";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { Clickable } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, { CartLineContainerStyles } from "@synd/App/Components/Cart/CartLineContainer";
import LinesHeader from "@synd/App/Components/Cart/LinesHeader";
import QtyField, { QtyFieldStyles } from "@synd/App/Components/Qty/QtyField";
import CartLineNotes from "@synd/Overrides/Widgets/Cart/CartLineNotes";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ProductImageBadges, { ProductImageBadgesStyles } from "@synd/Overrides/Components/ProductImageBadges";
import ProductCases from "@synd/App/Components/Cart/ProductCases";
import { isTabletScreen, parseBoolean } from "@synd/XDL/Utilities";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { xHasRole } from "@synd/App/Store/Selectors/GeneralSelectors";
import { USER_ROLES } from "@synd/XDL/constants";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const settings = getSettingsCollection(state);

    return {
        wishListSettings: settings.wishListSettings,
        enableVat: settings.productSettings.enableVat,
        vatPriceDisplay: settings.productSettings.vatPriceDisplay,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        canViewVmiOrder: isVmiAdmin(settings.orderSettings, session) || isVmiUser(settings.orderSettings, session),
        showPricing: state.context.showPricing,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isWholesaler: xHasRole(state, USER_ROLES.WHOLESALER_ADMIN),
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    // XNG-Changes
    productOrderPriceContainer?: InjectableCss;
    productInfoSectionOptionsLableText?: TypographyProps;
    productInfoSectionOptionsValueText?: TypographyProps;
    productPartNumbers?: ProductPartNumbersStyles;
    promotionsContainer?: InjectableCss;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    addToListLink?: LinkPresentationProps;
    promotionNameLabel?: TypographyPresentationProps;
    quantityTextField?: TextFieldPresentationProps;
    lableText?: TypographyPresentationProps;
    qtyOrderedField?: QtyFieldStyles;
    qtyShippedField?: QtyFieldStyles;
    productQtyContainer?: InjectableCss;
    priceHeading?: TypographyProps;
    priceText?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    priceWrapper?: InjectableCss;
    cartLineContainerStyles?: CartLineContainerStyles;
    imageBadges?: ProductImageBadgesStyles;
    rtPriceWrapper?: TypographyProps;
    rtPriceHeading?: TypographyProps;
    rtPriceText?: TypographyProps;
    rtUnitOfMeasure?: TypographyProps;
    reorderCartLineIcon?: ButtonPresentationProps;
    actionsWrapper?: InjectableCss;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 15px 0 0 0;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 8, 8],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [2, 2, 3, 2, 2],
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 12],

        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 10px;
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    productInfoQtyShippedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    productInfoSubtotalHeadingAndText: {
        wrapper: {
            css: css`
                padding-right: 10px;
            `,
        },
        heading: {
            variant: "legend",
            weight: "bold",
        },

        text: {
            weight: "bold",
            css: css`
                text-align: center;
            `,
        },
    },
    priceHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
            text: {
                weight: "bold",
            },
        },
    },
    customerProductNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    erpNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    manufacturerHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
};

// XNG-Changes: promotion to be in Order Line Product Info component
const OrderLineProductInfo = ({
    orderLine,
    order,
    styles,
    vmiLocationId,
    canViewVmiOrder = false,
    imageBadges,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    canViewVmiOrder?: boolean;
    imageBadges: BadgeModel[] | null;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <StyledWrapper {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsLableText}>{option.sectionName}</Typography>
                <Typography {...styles.productInfoSectionOptionsValueText}>{option.optionName}</Typography>
            </StyledWrapper>
        ));
    }

    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map((promotion, index) => (
                <StyledWrapper key={promotion.id}>
                    <Typography {...styles.promotionNameLabel}>{`${translate("Promotion")} #${index + 1}`}</Typography>
                    <Typography {...styles.orderLineInfoPromotionText}>{promotion.name}</Typography>
                </StyledWrapper>
            ));
    }

    return (
        <>
            {orderLine.brand && (
                <ProductBrand brand={orderLine.brand} showLogo={false} extendedStyles={styles.productBrandStyles} />
            )}
            {canViewVmiOrder && vmiLocationId && orderLine.isActiveVmiBin && (
                <VmiBinDetailTypeLink
                    title={orderLine.shortDescription}
                    locationId={vmiLocationId}
                    id={orderLine.vmiBinId}
                />
            )}
            {canViewVmiOrder && vmiLocationId && !orderLine.isActiveVmiBin && (
                <Typography as="p">{orderLine.shortDescription}</Typography>
            )}
            {(!canViewVmiOrder || !vmiLocationId) && (
                <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            )}
            <ProductPartNumbers
                productNumber={orderLine.productErpNumber}
                customerProductNumber={orderLine.customerName}
                manufacturerItem={orderLine.manufacturerItem}
                extendedStyles={styles.productPartNumbers}
                // XNG-Changes
                {...{
                    packSize: (orderLine as any).packSize,
                }}
                showProductNumber={false}
            />
            {/* XNG-Changes */}
            {imageBadges && <ProductImageBadges badgeList={imageBadges} extendedStyles={styles.imageBadges} />}

            {sectionOptions.length > 0 && (
                <StyledWrapper {...styles.productInfoSectionOptionsGridItem}>{sectionOptions}</StyledWrapper>
            )}

            {promotions && promotions.length > 0 && (
                <StyledWrapper {...styles.promotionsContainer}>{promotions}</StyledWrapper>
            )}
            {orderLine.vmiBinNumber && (
                <StyledWrapper {...styles.productInfoVmiBinNumberGridItem}>
                    <SmallHeadingAndText
                        {...styles.vmiBinNumberHeadingAndText}
                        heading={translate("Bin #")}
                        text={orderLine.vmiBinNumber}
                    />
                </StyledWrapper>
            )}
        </>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

// XNG-Change: splitting qty, price and total to follow the new structure pattern
const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
    isTabletScreen,
}: {
    orderLine: OrderLineModel;
    order?: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
    isTabletScreen?: boolean;
}) => {
    // XNG-Changes: Move promotions to OrderLineProductInfo

    return {
        Qty: (
            <StyledWrapper {...styles.productQtyContainer}>
                <StyledWrapper>
                    <QtyField
                        label={translate("QTY Ordered")}
                        editable={false}
                        value={orderLine.qtyOrdered}
                        extendedStyles={styles.qtyOrderedField}
                    />
                </StyledWrapper>
                {(!orderLine.properties.wholeSalerPrice ||
                    (orderLine.properties.wholeSalerPrice && !isTabletScreen)) && (
                    <StyledWrapper>
                        <QtyField
                            label={translate("QTY Shipped")}
                            editable={false}
                            value={orderLine.qtyShipped}
                            extendedStyles={styles.qtyShippedField}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        ),
        Price: (
            <StyledWrapper {...styles.productOrderPriceContainer} data-test-selector="productOrderPriceContainer">
                {showPricing && (
                    <>
                        <StyledWrapper {...styles.priceWrapper}>
                            <Typography {...styles.priceHeading}>{translate("Price/Unit")}</Typography>
                            <Typography {...styles.priceText}>
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.unitPriceWithVatDisplay
                                    : orderLine.unitPriceDisplay}
                            </Typography>
                            <Typography {...styles.unitOfMeasure}>
                                {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                            </Typography>
                        </StyledWrapper>

                        {/* XNG-Changes */}
                        {orderLine.properties.wholeSalerPrice && (
                            <StyledWrapper {...styles.rtPriceWrapper}>
                                <Typography {...styles.rtPriceHeading}>{translate("Your Price")}</Typography>
                                <StyledWrapper>
                                    <Typography {...styles.rtPriceText}>
                                        {orderLine.properties.wholeSalerPrice}
                                    </Typography>
                                    <Typography {...styles.rtUnitOfMeasure}>
                                        {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {orderLine.unitPriceDisplay +
                                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </StyledWrapper>
        ),
        Total: (
            <StyledWrapper {...styles.productOrderPriceContainer}>
                {showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                {...styles.subtotalWithoutVatHeadingAndText}
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={orderLine.extendedUnitNetPriceDisplay}
                            />
                        )}
                        <SmallHeadingAndText
                            {...styles.productInfoSubtotalHeadingAndText}
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                      ? `${translate("Total")} (${translate("Inc. VAT")})`
                                      : `${translate("Total")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.netPriceWithVatDisplay
                                    : orderLine.extendedUnitNetPriceDisplay
                            }
                        />
                    </>
                )}
            </StyledWrapper>
        ),
    };
};

// const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

// XNG-Changes: HTML structure
// const OrderLineNotes = ({ orderLine, extendedStyles }: { orderLine: OrderLineModel; extendedStyles?: any }) => {
//     return orderLine.notes ? (
//         <SmallHeadingAndText extendedStyles={extendedStyles} heading={translate("Line Notes")} text={orderLine.notes} />
//     ) : null;
// };

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    // XNG-Changes
    className?: string;
    enableVat: boolean;
    vatPriceDisplay: string;
    showPricing?: boolean;
    canViewVmiOrder?: boolean;
    isTabletScreen?: boolean;
    isWholesaler?: boolean;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        enableVat,
        vatPriceDisplay,
        showPricing,
        canViewVmiOrder = false,
        isTabletScreen,
        isWholesaler,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Changes
    const orderLineInfo = OrderLineInfoProto({
        orderLine,
        order,
        styles,
        enableVat,
        vatPriceDisplay,
        showPricing,
        isTabletScreen,
    });

    const isShareEntity = getIsShareEntity();

    const hasVmi = orderLine.isActiveProduct && !!order.vmiLocationId && orderLine.isActiveVmiBin;
    const hasProduct = orderLine.isActiveProduct && !!orderLine.productUri;
    const showProductLink = (!canViewVmiOrder && hasProduct) || (canViewVmiOrder && !hasVmi && hasProduct);
    const showVmiLink = canViewVmiOrder && hasVmi;
    const showNoLink = !showProductLink && !showVmiLink;

    // XNG-Changes
    // TODO:: handling reorder for each line (qtyOnHand is messing)
    const productContext: ProductContextModel = {
        product: {
            id: orderLine.productId,
            canAddToCart: orderLine.canAddToCart,
            productTitle: orderLine.description,
            badges: (orderLine as any).badges,
        } as ProductModel,
        productInfo: {
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
            inventory: (orderLine as any).inventory,
            failedToLoadInventory: (orderLine as any).failedToLoadInventory,
            pricing: {
                unitRegularBreakPrices: [],
                unitNetPrice: orderLine.unitNetPrice,
                unitNetPriceDisplay: orderLine.unitNetPriceDisplay,
                unitRegularPrice: orderLine.unitRegularPrice,
                unitRegularPriceDisplay: orderLine.unitRegularPriceDisplay,
            },
        } as unknown as ProductInfo,
    };

    const { imageBadges } = useWidgetBadges({
        productContext,
        showImageBadges: true,
        showTextBadges: true,
    });

    // XNG-Changes: using cartLine container global structure
    return (
        <ProductContext.Provider value={productContext}>
            <CartLineContainer
                data-test-selector={`orderLineCard${orderLine.productId}`}
                className={props.className}
                extendedStyles={styles.cartLineContainerStyles}
                includeActions={true}
            >
                <CartLineContainer.Image>
                    {showProductLink && (
                        <Clickable
                            {...styles.productImageLink}
                            href={orderLine.productUri}
                            aria-label="share entity clickable"
                        >
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={orderLine.mediumImagePath}
                                isShareEntity={isShareEntity}
                                altText={orderLine.altText}
                            />
                        </Clickable>
                    )}
                    {showVmiLink && (
                        <Clickable
                            {...styles.productImageLink}
                            href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                            aria-label="share entity clickable"
                        >
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={orderLine.mediumImagePath}
                                isShareEntity={isShareEntity}
                                altText={orderLine.altText}
                            />
                        </Clickable>
                    )}
                    {showNoLink && (
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                            altText={orderLine.altText}
                        />
                    )}
                </CartLineContainer.Image>
                <CartLineContainer.Description>
                    <OrderLineProductInfo
                        orderLine={orderLine}
                        styles={styles}
                        vmiLocationId={order.vmiLocationId}
                        order={order}
                        canViewVmiOrder={canViewVmiOrder}
                        imageBadges={imageBadges}
                    />

                    {!isTabletScreen && (
                        <CartLineNotes cart={{ canModifyOrder: false } as CartModel} cartNotes={orderLine.notes} />
                    )}
                </CartLineContainer.Description>
                <CartLineContainer.Cases>
                    {/* Xng-Changes */}
                    <ProductCases cases={(orderLine as any).casesSkid || "-"} />
                    {isTabletScreen && orderLine.properties.wholeSalerPrice && (
                        <QtyField
                            label={translate("QTY Shipped")}
                            editable={false}
                            value={orderLine.qtyShipped}
                            extendedStyles={styles.qtyShippedField}
                        />
                    )}
                </CartLineContainer.Cases>
                <CartLineContainer.Qty>{orderLineInfo.Qty}</CartLineContainer.Qty>
                <CartLineContainer.Price>{orderLineInfo.Price}</CartLineContainer.Price>
                <CartLineContainer.Total>{orderLineInfo.Total}</CartLineContainer.Total>
                <CartLineContainer.Remove>
                    {!isTabletScreen && (
                        <StyledWrapper {...styles.actionsWrapper}>
                            {/* XNG-Changes: hide add to list for prebook */}
                            {orderLine.canAddToWishlist && !parseBoolean(order.properties?.isPreBook) && (
                                <Link
                                    onClick={addToListClickHandler}
                                    {...styles.addToListLink}
                                    aria-label="Add to list"
                                >
                                    {translate("Add to List")}
                                </Link>
                            )}
                            {order.properties.showReOrderButton !== "false" && (
                                <ProductAddToCartButton
                                    notes={orderLine.notes}
                                    extendedStyles={orderLinesListStyles.reorderCartLineIcon}
                                    labelOverride={translate("Reorder")}
                                    disabled={(orderLine as any).inventory === undefined}
                                />
                            )}
                        </StyledWrapper>
                    )}
                </CartLineContainer.Remove>
                <CartLineContainer.Actions>
                    {isTabletScreen && (
                        <StyledWrapper {...styles.actionsWrapper}>
                            {orderLine.canAddToWishlist && (
                                <Link
                                    onClick={addToListClickHandler}
                                    {...styles.addToListLink}
                                    aria-label="Add to list"
                                >
                                    {translate("Add to List")}
                                </Link>
                            )}
                            {order.properties.showReOrderButton !== "false" && (
                                <ProductAddToCartButton
                                    notes={orderLine.notes}
                                    extendedStyles={orderLinesListStyles.reorderCartLineIcon}
                                    labelOverride={translate("Reorder")}
                                    disabled={(orderLine as any).inventory === undefined}
                                />
                            )}
                        </StyledWrapper>
                    )}
                    <CartLineNotes cart={{ canModifyOrder: false } as CartModel} cartNotes={orderLine.notes} />
                </CartLineContainer.Actions>
            </CartLineContainer>
        </ProductContext.Provider>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    enableVat,
    vatPriceDisplay,
    showPricing,
    canViewVmiOrder,
    // XNG-Changes
    isTabletScreen,
    isWholesaler,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    if (!order || !order.orderLines) {
        return null;
    }

    // XNG-Changes
    const totalQtyOrdered = order.orderLines.reduce((sum, line) => sum + (line.qtyOrdered || 0), 0);

    return (
        <StyledWrapper {...styles.gridContainer}>
            {!!order.orderLines.length && (
                <CartLinesListHeader
                    isCondensed={false}
                    hideCondensedSelector
                    productsCount={order.orderLines.length}
                    totalQtyOrdered={totalQtyOrdered}
                />
            )}
            <LinesHeader showMdScreenBorder={false} includeActions={true} />
            {order.orderLines.map(orderLine => (
                <OrderLineCard
                    key={orderLine.id}
                    orderLine={orderLine}
                    order={order}
                    wishListSettings={wishListSettings}
                    styles={styles}
                    setAddToListModalIsOpen={setAddToListModalIsOpen}
                    addToWishList={addToWishList}
                    enableVat={enableVat}
                    vatPriceDisplay={vatPriceDisplay}
                    showPricing={showPricing}
                    canViewVmiOrder={canViewVmiOrder}
                    // XNG-Changes
                    isTabletScreen={isTabletScreen}
                    isWholesaler={isWholesaler}
                />
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
